import { Box } from '@mui/material';
import { Typography, colors, Button } from '@sweep-io/sweep-design';
import { DIMENSION_AVATAR_SMALL, DIMENSION_AVATAR_VERY_SMALL } from '../../../constants';
import UserSummaryBasic from './UserSummaryBasic';
import type { InviteeInfo } from '../../../types/UserInfoTypes';
import { InviteeStatus } from '../../../types/UserInfoTypes';
import RestrictedTooltip from '../permissions/RestrictedTooltip';
import { useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { usePermissions } from '../../../permissions/usePermissions';

export interface InviteeSummaryProps {
  user: InviteeInfo;
  onResend: (user: InviteeInfo) => Promise<void>;
  onRemove: (email: string) => Promise<AxiosResponse<any, any> | boolean | undefined>;
}

const InviteeSummary = ({ user, onResend, onRemove }: InviteeSummaryProps) => {
  const { evaluatePermissions } = usePermissions();
  const [isAllowedBtnCreate, isAllowedBtnDelete] = evaluatePermissions([
    { permission: ['create:users:invite', 'delete:users:invite'] },
  ]);
  const [isLoadingResent, setIsLoadingResent] = useState(false);
  const [isLoadingUndo, setIsLoadingUndo] = useState(false);
  const isExpired = InviteeStatus.InviteeStatusExpired === user.status;
  const userHeader = isExpired ? 'Invitation expired' : 'Pending invitation';
  const { multiAccounts } = useFeatureToggle();

  return (
    <UserSummaryBasic
      className="user-summary"
      data-testid="user-summary"
      sx={{
        borderBottom: multiAccounts ? `1px solid ${colors.grey[120]}` : undefined,
        alignItems: multiAccounts ? 'center' : undefined,
        '&:hover, &:active': {
          backgroundColor: multiAccounts ? colors.grey[100] : undefined,
        },
        pr: multiAccounts ? '16px' : undefined,
        pl: multiAccounts ? '16px' : undefined,
      }}
    >
      <div className="user-summary-avatar">
        <Box
          sx={{
            width: `${multiAccounts ? DIMENSION_AVATAR_SMALL : DIMENSION_AVATAR_VERY_SMALL}px`,
            height: `${multiAccounts ? DIMENSION_AVATAR_SMALL : DIMENSION_AVATAR_VERY_SMALL}px`,
            borderRadius: '40px',
            backgroundColor: colors.grey[100],
            border: `1px solid ${colors.grey[120]}`,
            textAlign: 'center',
            '& h1': {
              lineHeight: `${multiAccounts ? DIMENSION_AVATAR_SMALL : DIMENSION_AVATAR_VERY_SMALL}px`,
            },
          }}
        />
      </div>
      <Box
        display="flex"
        gap="8px"
        flex="1 0"
        flexDirection="column"
        overflow="hidden"
        className="text-info"
        minHeight={multiAccounts ? '84px' : undefined}
        justifyContent={multiAccounts ? 'center' : undefined}
      >
        <Typography
          data-testid="user-summary-name"
          ellipsis
          variant="body-bold"
          color={colors.grey[700]}
        >
          {userHeader}
        </Typography>
        <Typography
          data-testid="user-summary-email"
          ellipsis
          variant="caption"
          color={colors.grey[700]}
        >
          {user.invitee}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          '& .MuiButtonBase-root': {
            minWidth: 'auto',
            paddingRight: 1,
          },
        }}
      >
        <RestrictedTooltip
          validation={{ type: 'explicit', allowed: isAllowedBtnDelete }}
          notAllowedTitle={'To undo members invitations, please contact your admin.'}
        >
          <Button
            size="medium"
            variant="flat-grey"
            disabled={!isAllowedBtnDelete || isLoadingResent}
            onClick={async () => {
              setIsLoadingUndo(true);
              await onRemove(user.invitee);
              setIsLoadingUndo(false);
            }}
            loading={isLoadingUndo}
          >
            Undo
          </Button>
        </RestrictedTooltip>

        {isExpired && (
          <RestrictedTooltip
            validation={{ type: 'query', queries: [{ permission: ['create:users:invite'] }] }}
            notAllowedTitle={'To resend members invitations, please contact your admin.'}
          >
            <Button
              size="medium"
              variant="flat-blue"
              disabled={!isAllowedBtnCreate || isLoadingUndo}
              onClick={async () => {
                setIsLoadingResent(true);
                await onResend(user);
                setIsLoadingResent(false);
              }}
              loading={isLoadingResent}
            >
              Resend
            </Button>
          </RestrictedTooltip>
        )}
      </Box>
    </UserSummaryBasic>
  );
};

export { InviteeSummary };
